import React, { useEffect, useState } from 'react'
import { Button, Flex, HStack, Spacer, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/store'
import { createDashboardRolesApi } from '../../utils/api-interface'
import { DashboardProfilesList, DashboardRolesList } from '../../services/network'
import { log } from '../../utils/logger'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { DashboardProfileRole } from '../../components/DashboardProfileRole/DashboardProfileRole'
import { CreateDashboardProfileRoleModal } from '../../components/CreateDashboardProfileRoleModal/CreateDashboardProfileRoleModal'
import axios from 'axios'
import { RetryDialog } from '../../components/RetryDialog/RetryDialog'

function Roles(): JSX.Element {
  const { t } = useTranslation()
  const [profiles, setProfiles] = useState<DashboardProfilesList>()
  const [isCreateProfileModalOpen, setIsCreateProfileModalOpen] = useState(false)
  const [roles, setRoles] = useState<DashboardRolesList>()
  const role = useSelector((state: RootState) => state.auth.role)
  const [errorData, setErrorData] = useState<any>(null)
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false)
  const handleCloseRetryDialog = (): void => {
    setIsRetryDialogOpen(false)
  }

  const fetchDashboardUsers = async (): Promise<void> => {
    try {
      const dashboardRolesApi = createDashboardRolesApi()
      const response = await dashboardRolesApi.getDashboardProfiles()
      log.debug(response)
      setProfiles(response.data)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          log.error('Error fetching users:', error.response.data)
          setErrorData(error.response.data)
        } else {
          log.error('No response from the server:', error.message)
        }
      } else {
        log.error('Unexpected error:', error)
      }
      setIsRetryDialogOpen(true)
    }
  }

  const fetchDashboardRoles = async (): Promise<void> => {
    try {
      const dashboardRolesApi = createDashboardRolesApi()
      const response = await dashboardRolesApi.getDashboardRoles()
      log.debug(response)
      setRoles(response.data)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          log.error('Error fetching roles:', error.response.data)
          setErrorData(error.response.data)
        } else {
          log.error('No response from the server:', error.message)
        }
      } else {
        log.error('Unexpected error:', error)
      }
      setIsRetryDialogOpen(true)
    }
  }

  useEffect(() => {
    fetchDashboardUsers()
    fetchDashboardRoles()
    eva.replace()
  }, [])

  const handleConfirmRetryDialog = (): void => {
    setIsRetryDialogOpen(false)
    fetchDashboardUsers()
    fetchDashboardRoles()
  }

  useEffect(() => {
    eva.replace()
  }, [profiles])

  return (
    <Flex bg={'greyNeutral100'} direction='column' padding={'40px 120px 20px 40px'} gap={'30px'} height={'100vh'} minH='100vh'>
      <HStack marginTop={'30px'} alignItems={'flex-start'} position={'relative'}>
        <Text fontSize='26px' fontWeight='700' lineHeight='31.2px' textAlign='left'>
          {t('roles')}
        </Text>
        <Spacer></Spacer>
        {role === process.env.REACT_APP_ROLE_ADMINISTRATOR && (
          <Button
            variant={'outline'}
            textOverflow={'ellipsis'}
            overflow={'hidden'}
            textAlign={'center'}
            minWidth={'125px'}
            color={'primary.400'}
            borderColor={'primary.400'}
            onClick={(): void => setIsCreateProfileModalOpen(true)}>
            {t('create_dashboard_profile')}
          </Button>
        )}
      </HStack>
      <Flex direction={'column'} gap={'14px'} overflow={'auto'}>
        {profiles?.dashboardProfiles &&
          roles &&
          profiles.dashboardProfiles.map((profile) => (
            <DashboardProfileRole key={profile.id} profile={profile} roles={roles} setProfiles={setProfiles}></DashboardProfileRole>
          ))}
      </Flex>
      <CreateDashboardProfileRoleModal
        isOpen={isCreateProfileModalOpen}
        onClose={(): void => {
          setIsCreateProfileModalOpen(false)
        }}
        profiles={profiles}
        setProfiles={setProfiles}
      />
      <RetryDialog isOpen={isRetryDialogOpen} onClose={handleCloseRetryDialog} onConfirm={handleConfirmRetryDialog} message={errorData} />
    </Flex>
  )
}

export { Roles }
